<template>
	<div>
		<div v-if="show" :class="slideInClass">
			<header>
				<div class="title-container">
					<div>
						<h4 v-html="computedTitle"/>
						<h3 v-if="subTitle" v-html="subTitle"/>
					</div>
					<div class="close-container">
						<a href="#" @click.prevent="closeFan(false)">
							<Icon icon="close"/>
						</a>
					</div>
				</div>
				<NoExperienceInfo
					v-if="headerInfo === headerInfos.applicationNoExperience"
				/>
				<div v-if="withKeyword" class="characteristic-fan--search">
					<SearchInput
						v-model="keyword"
						:characteristic-type="characteristicType"
						:placeholder="keywordPlaceholder || 'Suche... (mind. 3 Zeichen)'"
						:catalog="searchConfig ? searchConfig.catalog : undefined"
					/>
				</div>
			</header>
			<main class="scrollbar-check">
				<div v-if="isNothingAllowedOverallInline" class="characteristic-fan--nothing-allowed">
					<b-form-checkbox
						:checked="isNothingAllowedOverallChecked"
						@change="onNothingAllowedOverallChanged"
					>
						{{ nothingAllowedOverallText }}
					</b-form-checkbox>
					<div class="characteristic-fan--items-info">
						<template v-if="showMultipleHint">Oder <b>Mehrfachauswahl</b> möglich:</template>
						<template v-else>Oder wähle aus:</template>
					</div>
				</div>
				<div
					v-else-if="!isNothingAllowed && showMultipleHint"
					class="characteristic-fan--items-info"
				>
					<b>Mehrfachauswahl</b> möglich:
				</div>
				<CharacteristicItem
					v-if="isNothingAllowed && sourceCharacteristic"
					:characteristic="sourceCharacteristic"
					:level="1"
					nothing
				/>
				<div
					v-for="itemGroup in itemsGroupedByLetter"
					:key="itemGroup.id"
				>
					<Separation
						v-if="itemGroup.separation"
						:separation="itemGroup.separation"
						class="items-separation"
					/>
					<div
						v-for="(characteristics, letter) in itemGroup.items"
						:key="letter"
						class="letter-section"
					>
						<div class="letter">{{ letter }}</div>
						<div class="items">
							<CharacteristicItem
								v-for="characteristic in characteristics"
								:key="characteristic.id"
								:characteristic="characteristic"
								:level="1"
								:keyword="hasValidKeyword ? keyword : undefined"
							/>
						</div>
					</div>
				</div>
			</main>
			<footer>
				<Button
					text="OK"
					:disabled="!valid"
					icon="checkmark"
					@click="closeFan(true)"
				/>
			</footer>
		</div>
		<Parent
			v-for="(parent, i) in fanParents"
			:key="parent.id"
			:characteristic="parent"
			:level="i + 2"
		/>
		<CompletionCheckPopUp
			v-if="showCompletionCheckPopUp"
			:title="completionCheckConfig.label"
			:ok-button-text="completionCheckConfig.okButtonText"
			@abort="showCompletionCheckPopUp = false"
			@skip="closeFan(true, true)"
			@ok="closeFan(true, false)"
		>
			<template #info>
				<div v-html="completionCheckConfig.popUpInfo"/>
			</template>
		</CompletionCheckPopUp>
	</div>
</template>

<script>
import Icon from "@/vue/icon/Icon";
import {mapActions, mapMutations, mapState} from "vuex";
import CharacteristicItem from "./fan/CharacteristicItem"
import Parent from "./fan/Parent"
import _ from "lodash";
import Button from "@/vue/common/Button";
import {itemsValid} from "@/helper/characteristic/Edit";
import {getClasses} from "@/helper/layout/Fan";
import {OPEN_FAN_KEY} from "@/store/characteristic/fan";
import {isEmptyOrNull, maxLength} from "@/helper/String";
import {shorten} from "@/helper/characteristic/Shorten";
import SearchInput from "@/vue/common/form/SearchInput.vue";
import {group, isEnabled} from "@/helper/characteristic/GroupByLetter";
import NoExperienceInfo from "@/vue/application/characteristic/NoExperienceInfo.vue";
import {arrayIsNotEmpty} from "@/helper/Array";
import CompletionCheckPopUp from "@/vue/characteristic/completion-check/PopUp.vue";
import {hasSelectedChildsToLastLevel, isSelected} from "@/helper/characteristic/Characteristic";
import {uuid} from "vue-uuid";
import Separation from "@/vue/characteristic/separation/Separation.vue";
import * as Event from "@/helper/tracking/Event";
import EventData from "@/helper/tracking/EventData";

export const HEADER_INFO__APPLICATION_NO_EXPERIENCE = 'application-no-experience';

export default {

	components: {
		Separation,
		CompletionCheckPopUp,
		NoExperienceInfo,
		SearchInput,
		Button,
		CharacteristicItem,
		Parent,
		Icon,
	},

	data ()
	{
		return {
			keyword: '',
			headerInfos: {
				applicationNoExperience: HEADER_INFO__APPLICATION_NO_EXPERIENCE
			},
			showCompletionCheckPopUp: false
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		...mapState('characteristic/fan', [
			'show',
			'title',
			'selected',
			'subTitle',
			'titleFormats',
			'characteristicItems',
			'characteristicType',
			'withKeyword',
			'searchConfig',
			'withExperience',
			'withSelfAssessment',
			'withEducation',
			'withRequirement',
			'nothingAllowed',
			'sourceCharacteristic',
			'groupByLetter',
			'supplementsConfig',
			'showMultipleHint',
			'keywordPlaceholder',
			'headerInfo',
			'completionCheckConfig'
		]),
		...mapState('characteristic/fan', {
			'fanParents': 'parents',
			'initialKeyword': 'keyword',
		}),
		...mapState('layout', ['fanLevelClasses']),
		hasSelections ()
		{
			return arrayIsNotEmpty(this.selected);
		},
		hasSelectedAll ()
		{
			return this.hasSelections && this.selected.length === this.characteristicItems.length;
		},
		computedTitle ()
		{
			let title = this.title;

			if (this.titleFormats?.level && this.titleFormats.level[1])
			{
				title = this.titleFormats.level[1];
			}
			else if (this.titleFormats?.default)
			{
				title = this.titleFormats.default;
			}

			return (title || '').replaceAll('%title%', maxLength(this.title, 50));
		},
		slideInClass ()
		{
			let extraClasses = ['characteristic-fan'];

			if (this.characteristicType)
			{
				extraClasses.push(this.characteristicType.id);
			}

			return getClasses({
				fanLevelClasses: this.fanLevelClasses,
				key: OPEN_FAN_KEY,
				extraClasses
			});
		},
		isNothingAllowedOverallInline ()
		{
			return this.nothingAllowed?.overall?.inFan && !this.hasSelections;
		},
		isNothingAllowedOverallChecked ()
		{
			return Array.isArray(this.selected) && this.selected.length === 0;
		},
		nothingAllowedOverallText ()
		{
			return this.nothingAllowed.overall.text;
		},
		isNothingAllowed ()
		{
			return this.nothingAllowed
				&& this.nothingAllowed.onlyOverall !== true
				&& (this.nothingAllowed.minimumLevel || 1) <= 1;
		},
		isNothingAllowedOnlyOverall ()
		{
			return this.nothingAllowed?.onlyOverall === true;
		},
		filteredCharacteristicItems ()
		{
			if (this.hasValidKeyword)
			{
				return shorten(this.characteristicItems, this.keyword, this.searchConfig?.type);
			}

			return this.characteristicItems;
		},
		itemsGroupedByLetter ()
		{
			if (!isEnabled({groupByLetter: this.groupByLetter, characteristic: this.sourceCharacteristic}))
			{
				return [{
					id: uuid.v4(),
					items: {
						'': this.filteredCharacteristicItems
					}
				}];
			}

			return group({ items: this.filteredCharacteristicItems });
		},
		hasValidKeyword ()
		{
			return !isEmptyOrNull(this.keyword) && this.keyword.length >= 3;
		},
		somethingSelected ()
		{
			const items = _.cloneDeep(this.characteristicItems);

			if (this.sourceCharacteristic)
			{
				items.push(this.sourceCharacteristic);
			}

			return _
				.filter(
					items,
					item => isSelected(this.selected, item) || hasSelectedChildsToLastLevel(this.selected, item)
				)
				.length > 0;
		},
		valid ()
		{
			if (
				(this.isNothingAllowed || this.isNothingAllowedOnlyOverall || this.isNothingAllowedOverallInline)
				&& this.isNothingAllowedOverallChecked
			)
			{
				return true;
			}

			if (!this.somethingSelected)
			{
				return false;
			}

			return itemsValid({
				selected: this.selected,
				items: this.filteredCharacteristicItems,
				withExperience: this.withExperience,
				withSelfAssessment: this.withSelfAssessment,
				withEducation: this.withEducation,
				withRequirement: this.withRequirement,
				supplementsConfig: this.supplementsConfig
			});
		}
	},

	methods: {
		...mapActions('characteristic/fan', [
			'close',
		]),
		...mapMutations('characteristic/fan', [
			'setSelected',
		]),
		closeFan (confirmed, skipped)
		{
			this.keyword = '';

			if (
				confirmed
				&& this.hasSelections
				&& !this.hasSelectedAll
				&& this.completionCheckConfig?.enabled
				&& !this.showCompletionCheckPopUp
			)
			{
				this.showCompletionCheckPopUp = true;
				return;
			}

			this.showCompletionCheckPopUp = false;

			// never being skipped if everything selected
			if (this.hasSelectedAll)
			{
				skipped = false;
			}

			this.close({
				confirmed,
				skipped
			});
		},
		onNothingAllowedOverallChanged (checked)
		{
			let action = checked ? 'Hinzugefügt' : 'Entfernt';

			if (this.characteristicType)
			{
				action = action + ' (' + this.characteristicType.label + ')';
			}

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction(action)
					.setName(this.nothingAllowedOverallText)
			);

			this.setSelected(checked ? [] : undefined);
		}
	},

	watch: {
		show (show)
		{
			if (show)
			{
				this.keyword = this.initialKeyword || '';

				if (!isEmptyOrNull(this.keyword) && this.filteredCharacteristicItems.length === 0)
				{
					this.keyword = '';
				}
			}
		}
	}
}
</script>
